import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import {
  analyzeCampaignAiApi,
  BUDGET_ADVICE_LABEL,
  fmtDateTime,
  lastAnalysisApi,
  VERDICT_LABEL,
  type AiAnalysis,
} from "../campanhasApi";

const PRIORITY_LABEL = { ALTA: "Prioridade alta", MEDIA: "Prioridade média", BAIXA: "Prioridade baixa" } as const;
const TONE_PILL = { ok: "ecoPill", warn: "ecoPill cmpPill--warn", bad: "ecoPill cmpPill--bad", off: "ecoPill ecoPill--off" } as const;

/**
 * Análise da campanha feita pela IA, em linguagem simples: como está indo, o que
 * funcionou, o que fazer agora e o orçamento. A última fica guardada; analisar de
 * novo custa uma chamada à IA.
 */
const AiAnalysisPanel: React.FC<{ campaignId: string; hasData: boolean }> = ({ campaignId, hasData }) => {
  const [analysis, setAnalysis] = useState<AiAnalysis | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    lastAnalysisApi(campaignId)
      .then(setAnalysis)
      .catch(() => setAnalysis(null))
      .finally(() => setLoaded(true));
  }, [campaignId]);

  const analyze = async () => {
    try {
      setBusy(true);
      setAnalysis(await analyzeCampaignAiApi(campaignId));
      toast.success("Análise pronta.");
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  const v = analysis ? VERDICT_LABEL[analysis.verdict] : null;

  return (
    <section className="aiAnalysis" aria-label="Análise da Eco-IA">
      <div className="aiAnalysis__head">
        <h3 className="cmpPanel__title">
          <EcoIcon name="helpCircle" size={18} /> Análise da Eco-IA
        </h3>
        <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--primary" onClick={analyze} disabled={busy || !hasData}>
          {busy ? "Analisando..." : analysis ? "Analisar de novo" : "Analisar com IA"}
        </button>
      </div>

      {!loaded ? null : busy ? (
        <p className="cmpHint">A Eco-IA está lendo os números da campanha. Leva até um minuto.</p>
      ) : !analysis ? (
        <p className="cmpHint">
          {hasData
            ? "Peça a análise: a Eco-IA explica em palavras simples como a campanha está indo e o que fazer agora."
            : "A análise fica disponível quando a campanha tiver resultados."}
        </p>
      ) : (
        <div className="aiAnalysis__body">
          <p className="aiAnalysis__headline">
            <span className={TONE_PILL[v!.tone]}>{v!.label}</span> {analysis.headline}
          </p>
          <p className="aiText">{analysis.summary}</p>

          <div className="cmpGrid">
            {analysis.whatWorked.length ? (
              <div>
                <h4 className="aiReview__subtitle">O que funcionou</h4>
                <ul className="aiList">
                  {analysis.whatWorked.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {analysis.whatDidnt.length ? (
              <div>
                <h4 className="aiReview__subtitle">O que não funcionou</h4>
                <ul className="aiList">
                  {analysis.whatDidnt.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          {analysis.actions.length ? (
            <div>
              <h4 className="aiReview__subtitle">O que fazer agora</h4>
              <ol className="aiActions">
                {analysis.actions.map((a) => (
                  <li key={a.title}>
                    <strong>{a.title}</strong> <span className="cmpSub">{PRIORITY_LABEL[a.priority]}</span>
                    <p className="aiText">{a.detail}</p>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          <div className="cmpNotice cmpNotice--info">
            <EcoIcon name="dollar" size={16} />
            <span>
              <strong>{BUDGET_ADVICE_LABEL[analysis.budget.recommendation]}</strong>
              {analysis.budget.suggestedDailyReais > 0
                ? ` — sugestão: ${analysis.budget.suggestedDailyReais.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} por dia.`
                : "."}{" "}
              {analysis.budget.reason}
              {analysis.bestAdPosition ? ` O anúncio ${analysis.bestAdPosition} é o que traz os melhores candidatos.` : ""}
            </span>
          </div>

          {analysis.glossary.length ? (
            <details className="aiGlossary">
              <summary>O que querem dizer os termos</summary>
              <dl className="cmpDl">
                {analysis.glossary.map((g) => (
                  <React.Fragment key={g.term}>
                    <dt>{g.term}</dt>
                    <dd>{g.meaning}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </details>
          ) : null}

          <p className="cmpHint">
            Análise de {fmtDateTime(analysis.createdAt)}
            {analysis.createdBy ? `, pedida por ${analysis.createdBy}` : ""}. É uma sugestão: as ações continuam nos botões da campanha.
          </p>
        </div>
      )}
    </section>
  );
};

export default AiAnalysisPanel;
